import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Merge, Search } from "lucide-react";
import { Avatar, Button, Input, Modal, ModalFooter, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { fullName, formatPhone } from "@/lib/format";
import type { Contact } from "@/lib/types";

/**
 * Merge another contact INTO the current one. The current contact is the
 * winner / survivor; the picked contact is the loser and gets merged in and
 * deleted. This is irreversible, so we make the direction unmistakable and
 * gate the destructive call behind an explicit confirm step.
 *
 * Search reuses the same GET /contacts?q= endpoint the contacts list uses.
 * We exclude the winner from the results so you can't merge a contact into
 * itself.
 */
export function MergeDialog({
  open, onClose, winner, onMerged,
}: {
  open: boolean;
  onClose: () => void;
  winner: Contact;
  onMerged: () => void;
}) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<Contact | null>(null);
  const [merging, setMerging] = useState(false);

  // Reset all local state whenever the dialog opens so a previous session's
  // search/selection never leaks into a fresh merge.
  useEffect(() => {
    if (open) { setQ(""); setDebounced(""); setResults([]); setPicked(null); setMerging(false); }
  }, [open]);

  // Debounce the search box.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // Fetch matches for the debounced query. Only runs while open and with a
  // non-empty query so we don't dump the entire address book on first open.
  useEffect(() => {
    if (!open) return;
    if (!debounced) { setResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    api.get<{ contacts: Contact[] }>("/contacts", { q: debounced, limit: 20 })
      .then((d) => { if (!cancelled) setResults(d.contacts); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [open, debounced]);

  // Never offer the winner itself as a merge candidate.
  const candidates = useMemo(
    () => results.filter((c) => c.id !== winner.id),
    [results, winner.id],
  );

  const doMerge = async () => {
    if (!picked) return;
    setMerging(true);
    try {
      await api.post<{ ok: boolean; contact_id: string }>(`/contacts/${winner.id}/merge`, { from: picked.id });
      toast.success(`Merged ${fullName(picked)} into ${fullName(winner)}`);
      onMerged();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to merge contacts");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge duplicate contact"
      description={<>Find the duplicate to fold into <strong className="text-zinc-300">{fullName(winner)}</strong>.</>}
      size="md"
    >
      <div className="space-y-4">
        {/* Search */}
        <div>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            leftIcon={<Search className="h-4 w-4" />}
            autoFocus
          />
          <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-zinc-800/70 divide-y divide-zinc-800/60">
            {!debounced ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">Start typing to find a duplicate contact.</li>
            ) : searching ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">Searching…</li>
            ) : candidates.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">No other contacts match “{debounced}”.</li>
            ) : candidates.map((c) => {
              const active = picked?.id === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(c)}
                    className={
                      "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors " +
                      (active ? "bg-rose-500/10" : "hover:bg-zinc-900/50")
                    }
                  >
                    <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-zinc-100">{fullName(c)}</div>
                      <div className="truncate text-xs text-zinc-500">{c.email || formatPhone(c.phone) || "No email or phone"}</div>
                    </div>
                    {active && <Merge className="h-4 w-4 shrink-0 text-rose-300" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Irreversible-merge warning, only once a duplicate is picked. */}
        {picked && (
          <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-200">
              <AlertTriangle className="h-4 w-4" /> This can't be undone
            </div>
            <p className="text-sm text-amber-100/90">
              <strong>{fullName(picked)}</strong> will be merged into{" "}
              <strong>{fullName(winner)}</strong> — their messages, tasks, notes, and
              activity move over, and <strong>{fullName(picked)}</strong> is then permanently removed.
            </p>
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant="danger"
          leftIcon={<Merge className="h-4 w-4" />}
          onClick={doMerge}
          loading={merging}
          disabled={!picked}
        >
          Merge in {picked ? fullName(picked) : "contact"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
